/**
 * Rhythmic Dictation Drill — free-practice mode.
 *
 * Picks a random figure from the pattern library at the chosen difficulty,
 * builds the AudioPlan, and returns a drill round. Unlike the FSRS curriculum
 * generator (which always plays the same fixed pattern per item), the free-
 * drill randomises which figure is shown each round.
 */

import type { AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import {
  generateRhythmicDictation,
  type RhythmicDictationParams,
} from "./generators/rhythmic-dictation.js";
export type { RhythmicDictationGrading, RhythmicDictationRating } from "./generators/rhythmic-dictation.js";
export { gradeRhythmicGrid, rhythmicDictationRating } from "./generators/rhythmic-dictation.js";

// ─── Pattern libraries ────────────────────────────────────────────────────────

// Each pattern is { pattern, figure } — matches RhythmicDictationParams minus meter.
// meter is inferred from pattern length (8 → 4/4, 6 → 6/8).

const SIMPLE_PATTERNS: ReadonlyArray<{ pattern: string; figure: string }> = [
  { pattern: "10101010", figure: "Even quarters" },
  { pattern: "11101010", figure: "Beat 1 divided" },
  { pattern: "10111010", figure: "Beat 2 divided" },
  { pattern: "10101011", figure: "Beat 4 divided" },
  { pattern: "10011010", figure: "Dotted figure, beat 1" },
  { pattern: "10001010", figure: "Half + two quarters" },
  { pattern: "11111111", figure: "All eighths" },
  { pattern: "00101010", figure: "Rest on beat 1" },
];

const COMPOUND_PATTERNS: ReadonlyArray<{ pattern: string; figure: string }> = [
  { pattern: "100100", figure: "Even dotted quarters" },
  { pattern: "111100", figure: "Beat 1 divided" },
  { pattern: "100111", figure: "Beat 2 divided" },
  { pattern: "111111", figure: "All eighths" },
  { pattern: "101100", figure: "Dotted eighth + 16th, beat 1" },
  { pattern: "000100", figure: "Rest on beat 1" },
  { pattern: "110100", figure: "Eighth + quarter, beat 1" },
  { pattern: "100010", figure: "Hemiola cross-accent" },
];

const SYNCOPATION_PATTERNS: ReadonlyArray<{ pattern: string; figure: string }> = [
  { pattern: "01010100", figure: "Syncopation: and-of-1" },
  { pattern: "10011010", figure: "Syncopation: and-of-2" },
  { pattern: "10100110", figure: "Syncopation: and-of-3" },
  { pattern: "01010101", figure: "Off-beat pairs" },
  { pattern: "10110000", figure: "Quarter + tied eighths" },
  { pattern: "00100010", figure: "Backbeat" },
  { pattern: "10101001", figure: "And-of-4 pickup" },
  { pattern: "10010100", figure: "Short clave phrase" },
];

// ─── Types ────────────────────────────────────────────────────────────────────

export type RhythmicDrillDifficulty = "simple" | "compound" | "syncopation";

export interface RhythmicDictationExerciseConfig {
  difficulty: RhythmicDrillDifficulty;
  sessionLength: number;
}

export interface RhythmicDictationExerciseRound {
  params: RhythmicDictationParams;
  tempo: number;
  cellCount: number;
  audioPlan: AudioPlan;
  teachAudio: AudioPlan;
}

export const DEFAULT_RHYTHMIC_DICTATION_EXERCISE_CONFIG: RhythmicDictationExerciseConfig = {
  difficulty: "simple",
  sessionLength: 10,
};

// ─── Stats key / item ID ──────────────────────────────────────────────────────

export function rhythmicDrillStatsKey(config: RhythmicDictationExerciseConfig): string {
  return `rhythmic-dictation-drill|${config.difficulty}`;
}

const NODE_FOR_DIFFICULTY: Record<RhythmicDrillDifficulty, string> = {
  simple: "rhythmic-dictation-simple",
  compound: "rhythmic-dictation-compound",
  syncopation: "rhythmic-dictation-syncopation",
};

/**
 * Maps the played figure (difficulty + pattern) to its curriculum item suffix.
 * Keyed by difficulty because one pattern (10011010) is both simple `dotted-beat1`
 * and syncopation `synco-and2`. Mirrors the suffixes in `expandNode()` so each
 * figure credits its own card and free drill can advance all 8 items per node —
 * not a phantom `::1` that no curriculum item shares.
 */
const PATTERN_SUFFIX: Record<RhythmicDrillDifficulty, Record<string, string>> = {
  simple: {
    "10101010": "even-quarters",
    "11101010": "div-beat1",
    "10111010": "div-beat2",
    "10101011": "div-beat4",
    "10011010": "dotted-beat1",
    "10001010": "half-quarters",
    "11111111": "all-eighths",
    "00101010": "rest-beat1",
  },
  compound: {
    "100100": "even-dotted",
    "111100": "div-beat1",
    "100111": "div-beat2",
    "111111": "all-eighths",
    "101100": "dotted-8th-16th",
    "000100": "rest-beat1",
    "110100": "eighth-quarter",
    "100010": "hemiola",
  },
  syncopation: {
    "01010100": "synco-and1",
    "10011010": "synco-and2",
    "10100110": "synco-and3",
    "01010101": "offbeat-pairs",
    "10110000": "quarter-tied",
    "00100010": "backbeat",
    "10101001": "pickup-and4",
    "10010100": "clave",
  },
};

export function rhythmicDrillItemId(
  config: RhythmicDictationExerciseConfig,
  pattern: string,
): string {
  const node = NODE_FOR_DIFFICULTY[config.difficulty];
  const suffix = PATTERN_SUFFIX[config.difficulty][pattern];
  // Defensive: an unmapped pattern would orphan the card, so fall back to the
  // node's first item rather than invent a phantom suffix.
  return `${node}::${suffix ?? Object.values(PATTERN_SUFFIX[config.difficulty])[0]!}`;
}

// ─── Builder ─────────────────────────────────────────────────────────────────

export function buildRhythmicDictationExerciseRound(
  config: RhythmicDictationExerciseConfig,
  seed: number,
): RhythmicDictationExerciseRound {
  const rng = new Rng(seed);

  const library =
    config.difficulty === "syncopation" ? SYNCOPATION_PATTERNS :
    config.difficulty === "compound"    ? COMPOUND_PATTERNS    :
    SIMPLE_PATTERNS;

  const { pattern, figure } = rng.pick(library);
  const meter: "4/4" | "6/8" = pattern.length === 6 ? "6/8" : "4/4";
  const params: RhythmicDictationParams = { pattern, meter, figure };

  // Use a secondary seed for the generator to keep tempo independent of figure selection.
  const exercise = generateRhythmicDictation("drill", params, rng.int(0xffffffff));

  return {
    params,
    tempo: exercise.meta.rhythmTempo!,
    cellCount: pattern.length,
    audioPlan: exercise.audio,
    teachAudio: exercise.meta.teachAudio!,
  };
}
